import React, { useState, useEffect } from 'react';
import { X, Calculator, TrendingUp, TrendingDown, Minus, Sparkles, Save, RotateCcw } from 'lucide-react';
import axios from 'axios';

function WhatIfPlanner({ onClose, onApplyChanges }) {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [modifiedValues, setModifiedValues] = useState({});
  const [simulating, setSimulating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/calculations/what-if/metrics');
      setMetrics(response.data);
      
      // Initialize modified values with current values
      const initialValues = {};
      response.data.forEach(m => {
        if (m.current_value !== null) {
          initialValues[m.metric_id] = m.current_value;
        }
      });
      setModifiedValues(initialValues);
    } catch (err) {
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (metricId, value) => {
    setModifiedValues(prev => ({
      ...prev,
      [metricId]: value === '' ? null : parseFloat(value)
    }));
    // Clear previous result when values change
    setResult(null);
  };

  const resetToOriginal = (metricId) => {
    const metric = metrics.find(m => m.metric_id === metricId);
    if (metric) {
      setModifiedValues(prev => ({
        ...prev,
        [metricId]: metric.current_value
      }));
      setResult(null);
    }
  };

  const resetAll = () => {
    const originalValues = {};
    metrics.forEach(m => {
      if (m.current_value !== null) {
        originalValues[m.metric_id] = m.current_value;
      }
    });
    setModifiedValues(originalValues);
    setResult(null);
  };

  const runSimulation = async () => {
    try {
      setSimulating(true);
      setError('');

      // Find metrics that have changed from their original values
      const changedMetrics = metrics.filter(m => {
        const newValue = modifiedValues[m.metric_id];
        const originalValue = m.current_value;
        return newValue !== null && newValue !== undefined && newValue !== originalValue;
      });

      if (changedMetrics.length === 0) {
        setError('No changes to simulate. Modify at least one metric value.');
        return;
      }

      const hypotheticalValues = changedMetrics.map(m => ({
        metric_id: m.metric_id,
        value: modifiedValues[m.metric_id]
      }));

      const response = await axios.post('/calculations/what-if', {
        hypothetical_values: hypotheticalValues
      });

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const getChangeIndicator = (metricId) => {
    const metric = metrics.find(m => m.metric_id === metricId);
    if (!metric) return null;
    
    const original = metric.current_value;
    const current = modifiedValues[metricId];
    
    if (original === null || current === null || current === undefined) return null;
    if (current > original) return 'up';
    if (current < original) return 'down';
    return 'same';
  };

  const getDeltaIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-400" />;
    }
  };

  const getScoreColor = (delta) => {
    if (delta > 0) return 'text-green-600';
    if (delta < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // Group metrics by parent pillar
  const groupedMetrics = metrics.reduce((acc, metric) => {
    const pillarName = metric.parent_pillar_name || metric.pillar_name || 'Other';
    if (!acc[pillarName]) {
      acc[pillarName] = [];
    }
    acc[pillarName].push(metric);
    return acc;
  }, {});

  const pillarColors = {
    'Authenticity': 'border-purple-500 bg-purple-50',
    'Accountability': 'border-green-500 bg-green-50',
    'Community': 'border-blue-500 bg-blue-50'
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="what-if-modal">
        <div className="bg-white rounded-2xl p-8 max-w-5xl w-full mx-4">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="what-if-modal">
      <div className="bg-white rounded-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">What-If Scenario Planner</h2>
                <p className="text-white/80">Simulate metric changes and see projected Trust Score impact</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/80 hover:text-white transition"
              data-testid="close-what-if"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-[calc(90vh-120px)]">
          {/* Left: Metric Editor */}
          <div className="flex-1 p-6 overflow-y-auto border-r border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Adjust Metric Values</h3>
              <button
                onClick={resetAll}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                data-testid="reset-all"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {/* Grouped Metrics */}
            <div className="space-y-6">
              {Object.entries(groupedMetrics).map(([pillarName, pillarMetrics]) => (
                <div key={pillarName} className={`rounded-xl border-l-4 p-4 ${pillarColors[pillarName] || 'border-gray-300 bg-gray-50'}`}>
                  <h4 className="font-semibold text-gray-900 mb-4">{pillarName}</h4>
                  <div className="space-y-4">
                    {pillarMetrics.map((metric) => {
                      const changeIndicator = getChangeIndicator(metric.metric_id);
                      const hasChanged = changeIndicator && changeIndicator !== 'same';
                      
                      return (
                        <div 
                          key={metric.metric_id} 
                          className={`bg-white rounded-lg p-4 shadow-sm ${hasChanged ? 'ring-2 ring-orange-300' : ''}`}
                          data-testid={`metric-${metric.metric_id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-gray-900">{metric.metric_name}</p>
                              <p className="text-xs text-gray-500">{metric.description}</p>
                            </div>
                            {hasChanged && (
                              <button
                                onClick={() => resetToOriginal(metric.metric_id)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Reset to original"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <label className="text-xs text-gray-500 mb-1 block">
                                Current: {metric.current_value?.toFixed(1) || '—'}
                              </label>
                              <input
                                type="number"
                                step="0.1"
                                value={modifiedValues[metric.metric_id] ?? ''}
                                onChange={(e) => handleValueChange(metric.metric_id, e.target.value)}
                                placeholder={metric.current_value?.toString() || 'Enter value'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              />
                            </div>
                            {changeIndicator && changeIndicator !== 'same' && (
                              <div className="flex items-center gap-1">
                                {getDeltaIcon(changeIndicator)}
                                <span className={`text-sm font-medium ${changeIndicator === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                                  {((modifiedValues[metric.metric_id] || 0) - (metric.current_value || 0)).toFixed(1)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Results Panel */}
          <div className="w-full lg:w-96 p-6 bg-gray-50 overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Simulation Results</h3>
            
            {/* Simulate Button */}
            <button
              onClick={runSimulation}
              disabled={simulating}
              className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-6"
              data-testid="simulate-button"
            >
              {simulating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Simulating...
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5" />
                  Run Simulation
                </>
              )}
            </button>

            {/* Results */}
            {result && (
              <div className="space-y-4" data-testid="simulation-results">
                {/* Overall Score */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Trust Score Impact</h4>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-gray-400 text-lg">{result.current_score.toFixed(1)}</span>
                      <span className="text-gray-400 mx-2">→</span>
                      <span className="text-3xl font-bold text-gray-900">{result.projected_score.toFixed(1)}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${getScoreColor(result.score_delta)}`}>
                      {result.score_delta > 0 ? <TrendingUp className="w-5 h-5" /> : 
                       result.score_delta < 0 ? <TrendingDown className="w-5 h-5" /> : 
                       <Minus className="w-5 h-5" />}
                      <span className="font-bold">
                        {result.score_delta > 0 ? '+' : ''}{result.score_delta.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pillar Breakdown */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">Pillar Breakdown</h4>
                  <div className="space-y-3">
                    {['authenticity', 'accountability', 'community'].map(pillar => {
                      const current = result.current_pillar_scores[pillar] || 0;
                      const projected = result.projected_pillar_scores[pillar] || 0;
                      const delta = result.pillar_deltas[pillar] || 0;
                      
                      return (
                        <div key={pillar} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <span className="font-medium text-gray-900 capitalize">{pillar}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400">{current.toFixed(1)}</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-bold text-gray-900">{projected.toFixed(1)}</span>
                            {delta !== 0 && (
                              <span className={`text-sm ${getScoreColor(delta)}`}>
                                ({delta > 0 ? '+' : ''}{delta.toFixed(1)})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Metrics Changed */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                    Changes Applied ({result.metrics_changed.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {result.metrics_changed.map((change, idx) => (
                      <div key={idx} className="text-sm py-2 border-b border-gray-100 last:border-0">
                        <p className="font-medium text-gray-900">{change.metric_name}</p>
                        <p className="text-gray-500">
                          {change.current_value?.toFixed(1) || '—'} → {change.projected_value.toFixed(1)}
                          {change.delta && (
                            <span className={`ml-2 ${change.delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({change.delta > 0 ? '+' : ''}{change.delta.toFixed(1)})
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Completeness */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Data Completeness</span>
                    <span className="font-bold text-gray-900">{result.data_completeness.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Apply Changes Button */}
                {onApplyChanges && (
                  <button
                    onClick={() => onApplyChanges(result.metrics_changed)}
                    className="w-full py-3 px-6 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2"
                    data-testid="apply-changes"
                  >
                    <Save className="w-5 h-5" />
                    Apply These Changes
                  </button>
                )}
              </div>
            )}

            {!result && (
              <div className="text-center py-8">
                <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Adjust metric values on the left and click &quot;Run Simulation&quot; to see projected results.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WhatIfPlanner;
